import { useCallback, useEffect, useMemo, useState } from 'react';

type PwaPlatform = 'ios' | 'android' | 'desktop' | 'unknown';
type InstallResult = 'accepted' | 'dismissed' | 'unavailable';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

function getPlatform(userAgent: string): PwaPlatform {
  const ua = userAgent.toLowerCase();

  if (/iphone|ipad|ipod/.test(ua)) {
    return 'ios';
  }
  if (/android/.test(ua)) {
    return 'android';
  }
  if (/macintosh|windows|linux|cros/.test(ua)) {
    return 'desktop';
  }

  return 'unknown';
}

function checkStandalone() {
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return window.matchMedia('(display-mode: standalone)').matches || nav.standalone === true;
}

export function usePwaInstall() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(checkStandalone());
  const platform = useMemo<PwaPlatform>(() => getPlatform(window.navigator.userAgent), []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const onDisplayModeChange = () => setIsInstalled(checkStandalone());
    const onAppInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
    };
    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };

    mediaQuery.addEventListener('change', onDisplayModeChange);
    window.addEventListener('appinstalled', onAppInstalled);
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);

    return () => {
      mediaQuery.removeEventListener('change', onDisplayModeChange);
      window.removeEventListener('appinstalled', onAppInstalled);
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    };
  }, []);

  const install = useCallback(async (): Promise<InstallResult> => {
    if (!installPrompt) {
      return 'unavailable';
    }

    await installPrompt.prompt();
    const outcome = (await installPrompt.userChoice).outcome;
    if (outcome === 'accepted') {
      setInstallPrompt(null);
      return 'accepted';
    }

    return 'dismissed';
  }, [installPrompt]);

  return {
    platform,
    isInstallable: Boolean(installPrompt),
    isInstalled,
    install
  };
}
